import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import ContextMenu from 'src/components/common/context_menu/ContextMenu';
import { ConfirmationModal } from 'src/components/common/modal/ConfirmationModal';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import { DataTable } from 'src/components/common/table/DataTable';
import { buildQueryParams } from 'src/components/common/table/dataTableQueryBuilder';
import { IMenuItem } from 'src/components/common/types/contextMenuTypes';
import {
	IColumn,
	ITableQueryState,
} from 'src/components/common/types/tableTypes';
import { IAIModel } from 'src/features/ai_model/domain/types';
import {
	AI_MODEL_LOADING_KEY,
	getAIModels,
	deleteAIModel,
} from 'src/features/ai_model/store';
import { AppDispatch, RootState } from 'src/store';
import { formatTimestamp } from 'src/utils/dateUtils';

import CreateAIModelModal from './CreateAIModelModal';

const DATA_TABLE_ROWS_PER_PAGE =
	Number(process.env.REACT_APP_DATA_TABLE_ROWS_PER_PAGE) || 10;

export const AIModelTable: React.FC = () => {
	const { siteSlug } = useParams<{ siteSlug: string }>();
	const navigate = useNavigate();
	const dispatch = useDispatch<AppDispatch>();

	const isLoading = useSelector(
		(state: RootState) => state.loading.loadingStates[AI_MODEL_LOADING_KEY],
	);

	const [totalCount, setTotalCount] = useState<number>(0);
	const [queryState, setQueryState] = useState<ITableQueryState>({ page: 0 });
	const [selectedRow, setSelectedRow] = useState<IAIModel | null>(null);
	const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(
		null,
	);
	const [aiModels, setAiModels] = useState<IAIModel[]>([]);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

	const menuItems: IMenuItem[] = [
		{
			label: 'View',
			icon: <VisibilityOutlinedIcon />,
			onClick: () => {
				if (selectedRow) {
					navigate(`/site/${siteSlug}/ai-models/${selectedRow.id}`);
				}
			},
		},
		{
			label: 'Delete',
			icon: <DeleteOutlineOutlinedIcon color='error' />,
			onClick: () => setIsDeleteModalOpen(true),
			color: 'error.main',
		},
	];

	const columns: IColumn<IAIModel>[] = [
		{
			key: 'sn',
			label: 'S.N.',
			width: '60px',
			render: (_data, index) => {
				const page = queryState.page || 0;
				return page * DATA_TABLE_ROWS_PER_PAGE + index + 1;
			},
		},
		{
			key: 'name',
			label: 'Name',
			render: (data) => data.name,
		},
		{
			key: 'description',
			label: 'Description',
			render: (data) => data.description || '-',
		},
		{
			key: 'active',
			label: 'Active',
			filterable: true,
			filterParamKey: 'active',
			menuItems: [
				{ label: 'True', value: 'true' },
				{ label: 'False', value: 'false' },
			],
			render: (data) => (data.active ? 'Yes' : 'No'),
		},
		{
			key: 'created_at',
			label: 'Created At',
			sortable: true,
			render: (data) => formatTimestamp(data.created_at),
		},
		{
			key: 'actions',
			label: 'Actions',
			align: 'left',
			render: (data) => (
				<IconButton
					onClick={(e) => {
						handleMenuClick(e, data);
					}}
				>
					<MoreVertOutlinedIcon />
				</IconButton>
			),
		},
	];

	const handleCloseModal = () => {
		setIsModalOpen(false);
		if (selectedRow) setSelectedRow(null);
	};

	const handleMenuClick = (
		event: React.MouseEvent<HTMLElement>,
		data: IAIModel,
	) => {
		setSelectedRow(data);
		setAnchorElement(event.currentTarget);
	};

	const fetchAIModels = () => {
		if (!siteSlug) return;
		const params = buildQueryParams(queryState);

		dispatch(
			getAIModels({
				params,
				siteSlug: siteSlug,
				onSuccess: (data) => {
					setTotalCount(data.info.count);
					setAiModels(data.results);
				},
			}),
		);
	};

	const handleDeleteAIModel = () => {
		if (!siteSlug || !selectedRow) return;
		dispatch(
			deleteAIModel({
				id: selectedRow.id,
				siteSlug,
				onSuccess: () => {
					snackbar('AI model deleted successfully', 'success');
					setIsDeleteModalOpen(false);
					setSelectedRow(null);
					setQueryState({ page: 0 });
				},
			}),
		);
	};

	useEffect(() => {
		fetchAIModels();
	}, [queryState, siteSlug]);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 3,
				padding: 3,
				backgroundColor: 'white',
				borderRadius: '4px',
				marginTop: '20px',
			}}
		>
			<Box>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Typography
						variant='h6'
						sx={{
							fontSize: '20px',
							fontWeight: 500,
							color: 'rgba(0, 0, 0, 0.87)',
						}}
					>
						AI Models
					</Typography>
					<Box sx={{ width: '231px', height: '36px' }}>
						<PrimaryButton
							variant='contained'
							color='primary'
							size='medium'
							onClick={() => {
								setIsModalOpen(true);
							}}
							disabled={isLoading}
						>
							ADD AI MODEL
						</PrimaryButton>
					</Box>
				</Box>
			</Box>

			<DataTable
				columns={columns}
				data={aiModels}
				loadingKey={AI_MODEL_LOADING_KEY}
				emptyMessage='No AI model found'
				paginationInfo={{
					totalCount,
				}}
				queryState={queryState}
				onQueryChange={setQueryState}
			/>

			<CreateAIModelModal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				onSuccess={() => {
					setQueryState({ page: 0 });
					handleCloseModal();
				}}
			/>

			<ConfirmationModal
				modalOpen={isDeleteModalOpen}
				handleModalCLose={() => setIsDeleteModalOpen(false)}
				title='Delete AI Model'
				modalContent={`Are you sure you want to delete the AI model "${selectedRow?.name}"?`}
				handleYes={handleDeleteAIModel}
				handleNo={() => setIsDeleteModalOpen(false)}
				loadingKey={AI_MODEL_LOADING_KEY}
			/>

			<ContextMenu
				open={!!anchorElement}
				onClose={() => {
					setAnchorElement(null);
				}}
				anchorElement={anchorElement}
				clearAnchorElement={() => {
					setAnchorElement(null);
				}}
				items={menuItems}
			/>
		</Box>
	);
};

export default AIModelTable;
